import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar as CalIcon, ChevronLeft, ChevronRight, ChevronDown, Plus, X, Clock, Sparkles, RotateCcw, Pencil, Upload, Link, CheckCircle, Trash2, AlertTriangle, ArrowRightLeft, Copy, RefreshCw, Shield, Unlink, ExternalLink, Users, Printer } from 'lucide-react';
import { getHolidayMap } from '../utils/federalHolidays';
import { generateCalendarPdf } from '../utils/calendarPdf';
import { toast } from 'sonner';
import { useSyncRefresh, notifySyncUpdate } from '../hooks/useSyncRefresh';

import API from '../utils/api';
var BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

var COLOR_CYCLE = ['', 'me', 'coparent', 'exchange'];
var DAY_TYPES = {
  me: { label: 'My Overnights', gradient: '#3B82F6', text: '#3B82F6', light: '#3B82F620' },
  coparent: { label: 'Co-Parent Overnights', gradient: '#EC4899', text: '#EC4899', light: '#EC489920' },
  exchange: { label: 'Exchange Day', gradient: '#F97316', text: '#F97316', light: '#F9731620' },
};

var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
var EVENT_CATEGORIES = ['Exchange', 'Pickup', 'Dropoff', 'Medical', 'School', 'Activity', 'Holiday', 'Court', 'Other'];
var EVENT_COLORS = [
  { value: '', label: 'Blue', hex: '#3B82F6', bg: '#3B82F6' },
  { value: 'red', label: 'Red', hex: '#EF4444', bg: '#EF4444' },
  { value: 'green', label: 'Green', hex: '#10B981', bg: '#10B981' },
  { value: 'yellow', label: 'Yellow', hex: '#EAB308', bg: '#EAB308' },
  { value: 'orange', label: 'Orange', hex: '#F97316', bg: '#F97316' },
  { value: 'pink', label: 'Pink', hex: '#F472B6', bg: '#F472B6' },
  { value: 'magenta', label: 'Magenta', hex: '#D946EF', bg: '#D946EF' },
];
var EXCHANGE_TIME_PRESETS = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'];

var PATTERN_OPTIONS = [
  { value: '', label: 'Select a pattern...' },
  { value: 'every', label: 'Every week on this day' },
  { value: 'every-other', label: 'Every other week' },
  { value: 'every-third', label: 'Every third week' },
  { value: 'every-fourth', label: 'Every fourth week' },
];

function dateKey(y, m, d) {
  return y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}


/* ==================== CHANGE REQUESTS PANEL ==================== */

var CR_STATUS = {
  pending: { color: '#EAB308', bg: '#EAB30815', label: 'Pending' },
  approved: { color: '#10B981', bg: '#10B98115', label: 'Approved' },
  denied: { color: '#EF4444', bg: '#EF444415', label: 'Denied' },
};

function ChangeRequestsPanel() {
  var [data, setData] = useState({ sent: [], received: [] });
  var [loading, setLoading] = useState(true);
  var [showForm, setShowForm] = useState(false);
  var [submitting, setSubmitting] = useState(false);
  var [crTab, setCrTab] = useState('received');
  var [form, setForm] = useState({ coparent_email: '', request_type: 'swap', original_date: '', proposed_date: '', reason: '' });
  var [actionLoading, setActionLoading] = useState('');

  function loadCR() {
    axios.get(API + '/change-requests', { withCredentials: true })
      .then(function (res) { setData(res.data); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }

  useEffect(function () { loadCR(); }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.coparent_email || !form.original_date || submitting) return;
    setSubmitting(true);
    axios.post(API + '/change-requests', form, { withCredentials: true })
      .then(function () { setShowForm(false); setForm({ coparent_email: '', request_type: 'swap', original_date: '', proposed_date: '', reason: '' }); loadCR(); })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Failed'); })
      .finally(function () { setSubmitting(false); });
  }

  function handleAction(id, action) {
    setActionLoading(id);
    axios.patch(API + '/change-requests/' + id, { action: action }, { withCredentials: true })
      .then(function () { loadCR(); })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Failed'); })
      .finally(function () { setActionLoading(''); });
  }

  function handleCancel(id) {
    if (!window.confirm('Cancel this change request?')) return;
    setActionLoading(id);
    axios.delete(API + '/change-requests/' + id, { withCredentials: true })
      .then(function () { loadCR(); })
      .catch(function () {})
      .finally(function () { setActionLoading(''); });
  }

  function handleDuplicate(id) {
    setActionLoading(id);
    axios.post(API + '/change-requests/' + id + '/duplicate', {}, { withCredentials: true })
      .then(function () { loadCR(); })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Failed'); })
      .finally(function () { setActionLoading(''); });
  }

  var pendingCount = data.received.filter(function (r) { return r.status === 'pending'; }).length;

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-5" data-testid="change-requests-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-[#10B981]" /> Change Requests
        </h3>
        <button onClick={function () { setShowForm(!showForm); }}
          className="px-4 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg text-sm font-medium hover:opacity-90 flex items-center gap-2"
          data-testid="new-cr-btn">
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Cancel' : 'New Request'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card-surface p-5 space-y-4" data-testid="cr-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#6B6690] mb-1">Co-Parent Email</label>
              <input type="email" required value={form.coparent_email}
                onChange={function (e) { setForm(Object.assign({}, form, { coparent_email: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5"
                placeholder="coparent@email.com" data-testid="cr-email" />
            </div>
            <div>
              <label className="block text-xs text-[#6B6690] mb-1">Type</label>
              <select value={form.request_type}
                onChange={function (e) { setForm(Object.assign({}, form, { request_type: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5" data-testid="cr-type">
                <option value="swap">Date Swap</option>
                <option value="give">Give My Day</option>
                <option value="take">Request Extra Day</option>
                <option value="reschedule">Reschedule</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#6B6690] mb-1">Original Date</label>
              <input type="date" required value={form.original_date}
                onChange={function (e) { setForm(Object.assign({}, form, { original_date: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5" data-testid="cr-orig-date" />
            </div>
            <div>
              <label className="block text-xs text-[#6B6690] mb-1">Proposed Date</label>
              <input type="date" value={form.proposed_date}
                onChange={function (e) { setForm(Object.assign({}, form, { proposed_date: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5" data-testid="cr-prop-date" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#6B6690] mb-1">Reason</label>
            <textarea value={form.reason}
              onChange={function (e) { setForm(Object.assign({}, form, { reason: e.target.value })); }}
              className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5 h-20 resize-none"
              placeholder="Explain why..." data-testid="cr-reason" />
          </div>
          <button type="submit" disabled={submitting}
            className="px-5 py-2.5 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
            data-testid="cr-submit">
            {submitting ? 'Sending...' : 'Send Request'}
          </button>
        </form>
      )}

      <div className="flex gap-2">
        <button onClick={function () { setCrTab('received'); }}
          className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
            (crTab === 'received' ? 'bg-[#2D2560]/20 text-[#2D2560] border border-[#2D2560]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
          data-testid="cr-tab-received">
          Received ({data.received.length})
          {pendingCount > 0 && <span className="ml-2 w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#EAB308] text-black text-xs font-bold">{pendingCount}</span>}
        </button>
        <button onClick={function () { setCrTab('sent'); }}
          className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
            (crTab === 'sent' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
          data-testid="cr-tab-sent">
          Sent ({data.sent.length})
        </button>
      </div>

      <div className="space-y-3" data-testid="cr-list">
        {(crTab === 'received' ? data.received : data.sent).length === 0 ? (
          <div className="card-surface p-8 text-center">
            <ArrowRightLeft className="w-8 h-8 text-[#A8AAD4] mx-auto mb-2" />
            <p className="text-[#6B6690] text-sm">No {crTab} requests yet</p>
          </div>
        ) : (
          (crTab === 'received' ? data.received : data.sent).map(function (req) {
            var st = CR_STATUS[req.status] || CR_STATUS.pending;
            var isLoading = actionLoading === req.id;
            return (
              <div key={req.id} className="card-surface p-4" data-testid={'cr-' + req.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                      <span className="text-xs px-2 py-0.5 bg-white/5 rounded-full text-[#3A3566] capitalize">{req.request_type}</span>
                    </div>
                    <p className="text-sm text-[#1E1A3E] mt-1">
                      {crTab === 'received'
                        ? <span><strong className="text-[#1E1A3E]">{req.from_user_name}</strong> requests a change</span>
                        : <span>Sent to <strong className="text-[#1E1A3E]">{req.to_user_name}</strong></span>}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#6B6690]">
                      <span>Original: <strong className="text-[#3A3566]">{req.original_date}</strong></span>
                      {req.proposed_date && <span>Proposed: <strong className="text-[#3A3566]">{req.proposed_date}</strong></span>}
                    </div>
                    {req.reason && <p className="text-xs text-[#8C86C4] mt-1 italic">"{req.reason}"</p>}
                  </div>
                  <div className="flex-shrink-0 flex gap-2 flex-wrap">
                    {crTab === 'received' && req.status === 'pending' && (
                      <>
                        <button onClick={function () { handleAction(req.id, 'approve'); }} disabled={isLoading}
                          className="px-3 py-1.5 bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 rounded-lg text-xs font-medium hover:bg-[#10B981]/30 disabled:opacity-40"
                          data-testid={'cr-approve-' + req.id}>
                          <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Approve
                        </button>
                        <button onClick={function () { handleAction(req.id, 'deny'); }} disabled={isLoading}
                          className="px-3 py-1.5 bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 rounded-lg text-xs font-medium hover:bg-[#EF4444]/30 disabled:opacity-40"
                          data-testid={'cr-deny-' + req.id}>
                          <X className="w-3.5 h-3.5 inline mr-1" /> Deny
                        </button>
                      </>
                    )}
                    {crTab === 'sent' && req.status === 'pending' && (
                      <button onClick={function () { handleCancel(req.id); }} disabled={isLoading}
                        className="px-3 py-1.5 bg-white/5 text-[#3A3566] border border-white/10 rounded-lg text-xs font-medium hover:bg-white/10 disabled:opacity-40"
                        data-testid={'cr-cancel-' + req.id}>
                        Cancel
                      </button>
                    )}
                    <button onClick={function () { handleDuplicate(req.id); }} disabled={isLoading}
                      className="px-3 py-1.5 bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30 rounded-lg text-xs font-medium hover:bg-[#5A4E9E]/30 disabled:opacity-40"
                      data-testid={'cr-duplicate-' + req.id}>
                      <Copy className="w-3.5 h-3.5 inline mr-1" /> Duplicate
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


/* ==================== GOOGLE CALENDAR PANEL ==================== */

function GoogleCalendarPanel() {
  var [status, setStatus] = useState({ loading: true, configured: false, connected: false });
  var [calendars, setCalendars] = useState([]);
  var [calendarsLoaded, setCalendarsLoaded] = useState(false);
  var [busy, setBusy] = useState(false);

  var loadStatus = useCallback(function () {
    return axios.get(API + '/google-calendar/status', { withCredentials: true })
      .then(function (r) { setStatus(Object.assign({ loading: false }, r.data)); return r.data; })
      .catch(function () { setStatus({ loading: false, configured: false, connected: false }); });
  }, []);

  var loadCalendars = useCallback(function () {
    return axios.get(API + '/google-calendar/calendars', { withCredentials: true })
      .then(function (r) { setCalendars((r.data && r.data.calendars) || []); setCalendarsLoaded(true); })
      .catch(function (e) {
        var msg = (e && e.response && e.response.data && e.response.data.detail) || 'Could not load Google calendars';
        toast.error(msg);
      });
  }, []);

  useEffect(function () {
    loadStatus().then(function (s) { if (s && s.connected) loadCalendars(); });
    // If we just came back from OAuth (?gcal=connected), re-poll status & cleanup the URL.
    var params = new URLSearchParams(window.location.search);
    if (params.get('gcal') === 'connected') {
      toast.success('Google Calendar connected!');
      params.delete('gcal');
      var newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
      setTimeout(function () { loadStatus().then(function (s) { if (s && s.connected) loadCalendars(); }); }, 500);
    }
  }, [loadStatus, loadCalendars]);

  function connect() {
    setBusy(true);
    axios.get(API + '/google-calendar/connect', { withCredentials: true })
      .then(function (r) {
        if (r.data && r.data.auth_url) {
          window.location.href = r.data.auth_url;
        } else {
          toast.error('Could not start Google authorization.');
          setBusy(false);
        }
      })
      .catch(function () { toast.error('Could not start Google authorization.'); setBusy(false); });
  }

  function disconnect() {
    if (!window.confirm('Disconnect Google Calendar? Synced events from Google will be removed.')) return;
    setBusy(true);
    axios.post(API + '/google-calendar/disconnect', {}, { withCredentials: true })
      .then(function () { toast.success('Google Calendar disconnected.'); setCalendars([]); setCalendarsLoaded(false); return loadStatus(); })
      .catch(function () { toast.error('Disconnect failed.'); })
      .finally(function () { setBusy(false); });
  }

  function toggleCalendar(id) {
    var next = calendars.map(function (c) { return c.id === id ? Object.assign({}, c, { selected: !c.selected }) : c; });
    setCalendars(next);
  }

  function saveSelection() {
    var ids = calendars.filter(function (c) { return c.selected; }).map(function (c) { return c.id; });
    setBusy(true);
    axios.post(API + '/google-calendar/calendars/select', { calendar_ids: ids }, { withCredentials: true })
      .then(function () { toast.success('Calendar selection saved.'); })
      .catch(function () { toast.error('Could not save selection.'); })
      .finally(function () { setBusy(false); });
  }

  function syncNow() {
    setBusy(true);
    axios.post(API + '/google-calendar/sync', {}, { withCredentials: true })
      .then(function (r) {
        var n = (r.data && (r.data.events_synced || r.data.synced || 0)) || 0;
        toast.success('Synced ' + n + ' event' + (n === 1 ? '' : 's') + ' from Google.');
        notifySyncUpdate();
        loadStatus();
      })
      .catch(function (e) {
        var msg = (e && e.response && e.response.data && e.response.data.detail) || 'Sync failed.';
        toast.error(msg);
      })
      .finally(function () { setBusy(false); });
  }

  if (status.loading) {
    return (
      <div className="card-surface p-8 text-center" data-testid="gcal-loading">
        <CalIcon className="w-12 h-12 mx-auto mb-4 text-[#5A4E9E]/40" />
        <p className="text-sm text-[#3A3566]">Loading…</p>
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="card-surface p-8 text-center" data-testid="gcal-not-configured">
        <CalIcon className="w-12 h-12 mx-auto mb-4 text-[#5A4E9E]/40" />
        <h3 className="text-lg font-semibold text-[#1E1A3E] mb-2">Google Calendar Integration</h3>
        <p className="text-sm text-[#6B6690] max-w-md mx-auto">Not yet configured. Please contact support.</p>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="card-surface p-8 text-center" data-testid="gcal-connect">
        <CalIcon className="w-12 h-12 mx-auto mb-4 text-[#5A4E9E]" />
        <h3 className="text-lg font-semibold text-[#1E1A3E] mb-2">Connect Google Calendar</h3>
        <p className="text-sm text-[#6B6690] max-w-md mx-auto mb-6">
          Pull events from your Google calendars into Parentum so your custody schedule and your everyday commitments live side by side. Read-only — Parentum won't modify your Google calendar.
        </p>
        <button onClick={connect} disabled={busy}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white font-semibold hover:opacity-90 disabled:opacity-50"
          data-testid="gcal-connect-btn">
          <ExternalLink className="w-4 h-4" /> {busy ? 'Opening Google…' : 'Connect Google Calendar'}
        </button>
      </div>
    );
  }

  // Connected — show calendar selector + sync controls
  return (
    <div className="card-surface p-6 space-y-4" data-testid="gcal-connected">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-[#DDE1EE]">
        <div>
          <h3 className="text-base font-semibold text-[#1E1A3E] flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#10B981]" /> Connected as {status.google_email || 'Google account'}
          </h3>
          <p className="text-xs text-[#6B6690] mt-1">
            Last sync: {status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : 'never'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={syncNow} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#5A4E9E] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
            data-testid="gcal-sync-btn">
            <RefreshCw className={'w-3.5 h-3.5 ' + (busy ? 'animate-spin' : '')} /> Sync now
          </button>
          <button onClick={disconnect} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#EF4444]/30 text-[#EF4444] text-xs font-medium hover:bg-[#EF4444]/10 disabled:opacity-50"
            data-testid="gcal-disconnect-btn">
            <Unlink className="w-3.5 h-3.5" /> Disconnect
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-[#3A3566]">Calendars to sync</h4>
          {calendarsLoaded && (
            <button onClick={saveSelection} disabled={busy}
              className="text-xs font-medium text-[#5A4E9E] hover:underline disabled:opacity-50"
              data-testid="gcal-save-selection">
              Save selection
            </button>
          )}
        </div>
        {!calendarsLoaded ? (
          <button onClick={loadCalendars} className="text-xs text-[#5A4E9E] hover:underline" data-testid="gcal-load-cals">
            Load calendars
          </button>
        ) : calendars.length === 0 ? (
          <p className="text-xs text-[#6B6690]">No calendars found in your Google account.</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {calendars.map(function (cal) {
              return (
                <label key={cal.id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-[#F6F7FC] cursor-pointer"
                  data-testid={'gcal-cal-' + cal.id}>
                  <input type="checkbox" checked={!!cal.selected} onChange={function () { toggleCalendar(cal.id); }}
                    className="accent-[#5A4E9E]" />
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: cal.background_color || '#5A4E9E' }} />
                  <span className="text-sm text-[#1E1A3E] flex-1 truncate">{cal.summary || cal.id}</span>
                  {cal.primary && <span className="text-[10px] uppercase font-bold text-[#5A4E9E]">Primary</span>}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


var CalendarPage = function() {
  var now = new Date();
  var [month, setMonth] = useState(now.getMonth());
  var [year, setYear] = useState(now.getFullYear());
  var [assignments, setAssignments] = useState({});
  var [events, setEvents] = useState([]);
  var [showPrintModal, setShowPrintModal] = useState(false);
  var [printScope, setPrintScope] = useState('month'); // 'month' | 'year' | 'multi-year'
  var [printYear, setPrintYear] = useState(now.getFullYear());
  var [printMonth, setPrintMonth] = useState(now.getMonth());
  var [printYearsAhead, setPrintYearsAhead] = useState(1);
  var [printing, setPrinting] = useState(false);
  var [showEventForm, setShowEventForm] = useState(false);
  var [saving, setSaving] = useState(false);
  var [editingEvent, setEditingEvent] = useState(null);
  // Reschedule modal state — single event or "this & future" within a recurrence group.
  var [reschedule, setReschedule] = useState(null); // { event, newDate, newTime, scope, notify }
  var [rescheduleSaving, setRescheduleSaving] = useState(false);

  var openReschedule = function(ev) {
    setReschedule({
      event: ev,
      newDate: ev.start_date || '',
      newTime: ev.start_time || '',
      scope: 'single',
      notify: !!coparentLink,
    });
  };

  var submitReschedule = async function() {
    if (!reschedule || !reschedule.event || !reschedule.newDate) return;
    setRescheduleSaving(true);
    try {
      var r = await axios.post(
        API + '/calendar/events/' + reschedule.event.id + '/reschedule',
        {
          new_date: reschedule.newDate,
          new_time: reschedule.newTime || null,
          scope: reschedule.scope,
          notify_coparent: reschedule.notify,
        },
        { withCredentials: true }
      );
      var count = (r.data && r.data.updated_count) || 0;
      var sentToCP = !!(r.data && r.data.share_id);
      toast.success(
        'Rescheduled ' + count + ' event' + (count === 1 ? '' : 's')
          + (sentToCP ? ' · co-parent notified' : '')
      );
      setReschedule(null);
      loadData();
    } catch (e) {
      var msg = (e && e.response && e.response.data && e.response.data.detail) || 'Could not reschedule event.';
      toast.error(msg);
    }
    setRescheduleSaving(false);
  };
  var [eventForm, setEventForm] = useState({ title: '', category: 'Exchange', start_date: '', start_time: '', description: '', notes: '', color: '', recurrence: '', recurrence_days: [], recurrence_end_date: '', cancelled: false });
  var [coparentLink, setCoparentLink] = useState(null);
  var [pendingShares, setPendingShares] = useState([]);
  var [sharedCal, setSharedCal] = useState(null);
  var [sentShares, setSentShares] = useState([]);
  var [sendToCoparent, setSendToCoparent] = useState(false);

  // AI prompt
  var [aiPrompt, setAiPrompt] = useState('');
  var [aiLoading, setAiLoading] = useState(false);

  // Pattern dropdown
  var [patternColor, setPatternColor] = useState('me');
  var [patternDays, setPatternDays] = useState([]);
  var [patternFreq, setPatternFreq] = useState('');
  var [patternStart, setPatternStart] = useState(new Date().toISOString().slice(0, 10));
  var [patternExchangeTime, setPatternExchangeTime] = useState('');
  var [lastPatternSnapshot, setLastPatternSnapshot] = useState(null);
  var [lastExchangeTimesSnapshot, setLastExchangeTimesSnapshot] = useState(null);

  // Exchange times per DOW
  var [exchangeTimes, setExchangeTimes] = useState(['', '', '', '', '', '', '']);

  // Import state
  var [showImport, setShowImport] = useState(false);
  var [importTab, setImportTab] = useState('file');
  var [importUrl, setImportUrl] = useState('');
  var [importing, setImporting] = useState(false);
  var [importResult, setImportResult] = useState(null);
  var [calTab, setCalTab] = useState(function () {
    if (typeof window !== 'undefined') {
      var params = new URLSearchParams(window.location.search);
      if (params.get('gcal') === 'connected') {
        window.history.replaceState({}, '', window.location.pathname);
        return 'google';
      }
    }
    return 'calendar';
  });
  var [deletedEvents, setDeletedEvents] = useState([]);
  var [deletedOpen, setDeletedOpen] = useState(false);
  var [selectedDeleted, setSelectedDeleted] = useState([]);
  var [clearMode, setClearMode] = useState('');
  var [clearConfirmText, setClearConfirmText] = useState('');

  // School times
  var [schoolTimes, setSchoolTimes] = useState({});

  // AI daily summaries cache
  var [daySummaries, setDaySummaries] = useState({});
  var [summaryLoading, setSummaryLoading] = useState(false);
  var [selectedDay, setSelectedDay] = useState(null);
  var [dismissedHolidays, setDismissedHolidays] = useState([]);
  // Holiday map spans the current calendar year ±1 so January/December rollovers stay populated.
  var holidayMap = React.useMemo(function () {
    return getHolidayMap(year - 1, year + 1, dismissedHolidays);
  }, [year, dismissedHolidays]);
  useEffect(function() { loadData(); loadDeleted(); loadSchoolTimes(); loadCoparentLink(); loadDismissedHolidays(); }, []);

  // Sync: refresh on tab focus, app resume, network reconnect
  var refreshCalendar = useCallback(function() { loadData(); loadDeleted(); loadSchoolTimes(); loadCoparentLink(); loadPendingShares(); }, []);
  useSyncRefresh(refreshCalendar);

  var loadDismissedHolidays = async function() {
    try {
      var r = await axios.get(API + '/calendar/holidays/dismissed', { withCredentials: true });
      setDismissedHolidays((r.data && r.data.dismissed) || []);
    } catch (e) { console.error(e); }
  };

  var dismissHoliday = async function(holidayId) {
    try {
      var r = await axios.post(API + '/calendar/holidays/dismiss', { holiday_id: holidayId }, { withCredentials: true });
      setDismissedHolidays((r.data && r.data.dismissed) || []);
    } catch (e) { console.error(e); }
  };

  var loadCoparentLink = async function() {
    try {
      var r = await axios.get(API + '/coparent/link', { withCredentials: true });
      var link = r.data && r.data.link;
      var isActive = link && link.status === 'active';
      setCoparentLink(isActive ? link : null);
      if (isActive) {
        try {
          var sc = await axios.get(API + '/coparent/shared-calendar', { withCredentials: true });
          setSharedCal(sc.data);
        } catch (e2) { setSharedCal(null); }
      } else {
        setSharedCal(null);
      }
    } catch (e) { console.error(e); setCoparentLink(null); setSharedCal(null); }
  };

  // Pending invites are polled globally by usePendingSharesNotifier (mounted in Dashboard).
  // We subscribe to its published events here so we always render the latest list without a local poll.
  var loadPendingShares = async function() {
    try {
      var r = await axios.get(API + '/calendar/shared-events/pending', { withCredentials: true });
      setPendingShares(r.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(function () {
    var onUpdate = function (e) { setPendingShares(e.detail || []); };
    window.addEventListener('pending-shares-updated', onUpdate);
    // Do an initial fetch for users that land on calendar before the global poll has fired.
    loadPendingShares();
    return function () { window.removeEventListener('pending-shares-updated', onUpdate); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Allow Dashboard (via navigate-calendar-invites) to open the Pending Invites tab directly.
  useEffect(function () {
    var onSetTab = function (e) {
      if (e.detail && typeof e.detail === 'string') setCalTab(e.detail);
    };
    window.addEventListener('set-calendar-tab', onSetTab);
    return function () { window.removeEventListener('set-calendar-tab', onSetTab); };
  }, []);

  var respondToShare = async function(shareId, action) {
    try {
      await axios.post(API + '/calendar/shared-events/' + shareId + '/respond', { action: action }, { withCredentials: true });
      // Ask the global watcher to refresh — it will update state for all subscribers.
      window.dispatchEvent(new CustomEvent('pending-shares-refresh'));
      loadPendingShares();
      if (action === 'accept') {
        toast.success('Event added to your calendar');
        loadData();
      } else {
        toast.success('Invite declined');
      }
    } catch (e) { console.error(e); toast.error('Could not respond to invite.'); }
  };

  var respondToSharesBulk = async function(action) {
    if (pendingShares.length === 0) return;
    var verb = action === 'accept' ? 'accept' : 'decline';
    if (!window.confirm('Are you sure you want to ' + verb + ' all ' + pendingShares.length + ' pending event invite' + (pendingShares.length === 1 ? '' : 's') + '?')) return;
    try {
      var ids = pendingShares.map(function (p) { return p.id; });
      var results = await Promise.allSettled(ids.map(function (id) {
        return axios.post(API + '/calendar/shared-events/' + id + '/respond', { action: action }, { withCredentials: true });
      }));
      var successes = results.filter(function (r) { return r.status === 'fulfilled'; }).length;
      var failures = results.length - successes;
      window.dispatchEvent(new CustomEvent('pending-shares-refresh'));
      loadPendingShares();
      if (action === 'accept') loadData();
      if (successes > 0) {
        toast.success(
          (action === 'accept' ? 'Accepted ' : 'Declined ') +
          successes + ' invite' + (successes === 1 ? '' : 's') +
          (action === 'accept' ? ' — events added to your calendar' : '')
        );
      }
      if (failures > 0) {
        toast.error('Failed to process ' + failures + ' invite' + (failures === 1 ? '' : 's'));
      }
    } catch (e) {
      console.error(e);
      toast.error('Bulk action failed.');
    }
  };

  var loadData = async function() {
    try {
      var [aRes, eRes] = await Promise.all([
        axios.get(API + '/calendar/assignments', { withCredentials: true }),
        axios.get(API + '/calendar', { withCredentials: true })
      ]);
      setAssignments(aRes.data.assignments || {});
      setEvents(eRes.data || []);
    } catch (e) { console.error(e); }
  };

  var loadDeleted = async function() {
    try {
      var r = await axios.get(API + '/calendar/recently-deleted', { withCredentials: true });
      setDeletedEvents(r.data || []);
    } catch (e) { console.error(e); }
  };

  var loadSchoolTimes = async function() {
    try {
      var r = await axios.get(API + '/calendar/school-times', { withCredentials: true });
      setSchoolTimes((r.data && r.data.school_times) || {});
    } catch (e) { console.error(e); }
  };

  var saveSchoolTimes = async function(newTimes) {
    setSchoolTimes(newTimes);
    try {
      await axios.post(API + '/calendar/school-times', { school_times: newTimes }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  var fetchDaySummary = async function(dateStr) {
    if (daySummaries[dateStr]) { setSelectedDay(dateStr); return; }
    setSummaryLoading(true);
    setSelectedDay(dateStr);
    try {
      var r = await axios.post(API + '/calendar/day-summary', { date: dateStr }, { withCredentials: true });
      var updated = Object.assign({}, daySummaries);
      updated[dateStr] = r.data;
      setDaySummaries(updated);
    } catch (e) { console.error(e); }
    setSummaryLoading(false);
  };

  var saveAssignments = async function(newA) {
    setSaving(true);
    try {
      await axios.post(API + '/calendar/assignments', { assignments: newA }, { withCredentials: true });
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  // Click-to-rotate: cycle through empty → me → coparent → exchange → empty
  var rotateDay = function(day) {
    var key = dateKey(year, month, day);
    var cur = assignments[key] || '';
    var nextIdx = (COLOR_CYCLE.indexOf(cur) + 1) % COLOR_CYCLE.length;
    var next = Object.assign({}, assignments);
    if (COLOR_CYCLE[nextIdx] === '') { delete next[key]; }
    else { next[key] = COLOR_CYCLE[nextIdx]; }
    setAssignments(next);
    saveAssignments(next);
  };

  // AI prompt handler
  var handleAiPrompt = async function() {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      var res = await axios.post(API + '/calendar/ai-schedule', { prompt: aiPrompt }, { withCredentials: true });
      setAssignments(res.data.assignments || {});
      setAiPrompt('');
    } catch (e) {
      console.error(e);
      alert('Could not apply schedule. Try being more specific, e.g. "I have kids Mon-Wed, coparent has Thu-Sun, exchange days on Wednesday and Sunday"');
    }
    setAiLoading(false);
  };

  // Pattern applicator
  var togglePatternDay = function(dayIdx) {
    setPatternDays(function(prev) {
      if (prev.includes(dayIdx)) return prev.filter(function(d) { return d !== dayIdx; });
      return prev.concat([dayIdx]);
    });
  };

  var applyPattern = function() {
    if (!patternFreq || patternDays.length === 0) return;
    var freqWeeks = { every: 1, 'every-other': 2, 'every-third': 3, 'every-fourth': 4 };
    var interval = freqWeeks[patternFreq] || 1;

    // Save snapshots for undo (assignments + exchange times).
    setLastPatternSnapshot(Object.assign({}, assignments));
    setLastExchangeTimesSnapshot(exchangeTimes.slice());

    var next = Object.assign({}, assignments);
    var startDate = new Date(patternStart + 'T00:00:00');
    var endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 12);

    patternDays.forEach(function(patternDay) {
      var d = new Date(startDate);
      while (d.getDay() !== patternDay && d <= endDate) { d.setDate(d.getDate() + 1); }
      var weekCount = 0;
      while (d <= endDate) {
        if (weekCount % interval === 0) {
          var k = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
          next[k] = patternColor;
        }
        d.setDate(d.getDate() + 7);
        weekCount++;
      }
    });

    setAssignments(next);
    saveAssignments(next);

    // If user picked an exchange time AND this pattern is for exchange days,
    // propagate the time to the per-DOW table so it shows on the calendar.
    if (patternColor === 'exchange' && patternExchangeTime) {
      var nextTimes = exchangeTimes.slice();
      patternDays.forEach(function(d) { nextTimes[d] = patternExchangeTime; });
      setExchangeTimes(nextTimes);
    }

    setPatternFreq('');
  };

  var undoPattern = function() {
    if (!lastPatternSnapshot) return;
    setAssignments(lastPatternSnapshot);
    saveAssignments(lastPatternSnapshot);
    if (lastExchangeTimesSnapshot) setExchangeTimes(lastExchangeTimesSnapshot);
    setLastPatternSnapshot(null);
    setLastExchangeTimesSnapshot(null);
  };

  var clearDay = function(dayKey) {
    var next = Object.assign({}, assignments);
    delete next[dayKey];
    setAssignments(next);
    saveAssignments(next);
  };

  var clearAll = async function() {
    if (clearConfirmText !== 'DELETE ALL EVENTS') {
      alert('Please type "DELETE ALL EVENTS" to confirm.');
      return;
    }
    try {
      await axios.post(API + '/calendar/clear-all', { confirmation: 'DELETE ALL EVENTS' }, { withCredentials: true });
      setAssignments({});
      setClearMode('');
      setClearConfirmText('');
      loadData();
      loadDeleted();
    } catch (err) { console.error(err); }
  };

  var clearMonth = async function() {
    if (!window.confirm('Clear all events for ' + MONTHS[month] + ' ' + year + '? Events move to Recently Deleted for 30 days.')) return;
    try {
      await axios.post(API + '/calendar/clear-month', { year: year, month: month + 1 }, { withCredentials: true });
      loadData();
      loadDeleted();
    } catch (err) { console.error(err); }
  };

  // ===== Print Calendar to PDF =====
  // Pulls fresh events/change-requests/holidays straight from the API to make
  // sure the exported PDF reflects the latest server-side state, regardless
  // of which sub-panel the user has open in the UI.
  var handlePrintCalendar = async function() {
    setPrinting(true);
    try {
      // Determine the year range we need to fetch for.
      var fetchYears = [];
      if (printScope === 'month') fetchYears = [printYear];
      else if (printScope === 'year') fetchYears = [printYear];
      else if (printScope === 'multi-year') {
        for (var k = 0; k < printYearsAhead; k++) fetchYears.push(printYear + k);
      }

      // Holidays — federalHolidays util resolves these client-side per year.
      var holidays = [];
      fetchYears.forEach(function (yy) {
        var map = getHolidayMap(yy, yy) || {};
        Object.keys(map).forEach(function (k) {
          var h = map[k];
          holidays.push({ date: k, name: h.name || k });
        });
      });

      // Events + change requests fetched server-side. The /calendar endpoint
      // returns the full list which we'll filter client-side per month in the
      // PDF generator.
      var reqs = [
        axios.get(API + '/calendar', { withCredentials: true }).catch(function () { return { data: [] }; }),
        axios.get(API + '/change-requests', { withCredentials: true }).catch(function () { return { data: [] }; }),
      ];
      var results = await Promise.all(reqs);
      var allEvents = results[0].data || [];
      // /change-requests returns { sent: [...], received: [...] } — flatten so
      // the PDF generator can simply filter by month.
      var crRaw = results[1].data || {};
      var changeRequests = [].concat(crRaw.sent || [], crRaw.received || []);

      await generateCalendarPdf({
        scope: printScope,
        year: printYear,
        monthStart: { year: printYear, month: printMonth },
        yearsAhead: printYearsAhead,
        events: allEvents,
        changeRequests: changeRequests,
        holidays: holidays,
      });
      setShowPrintModal(false);
      toast.success('Calendar PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    } finally {
      setPrinting(false);
    }
  };

  var handleAddEvent = async function(e) {
    e.preventDefault();
    try {
      var createdEventId = null;
      var createdEventIds = null; // populated for recurring creates
      if (editingEvent) {
        await axios.patch(API + '/calendar/' + editingEvent, eventForm, { withCredentials: true });
        createdEventId = editingEvent;
      } else {
        var createRes = await axios.post(API + '/calendar', eventForm, { withCredentials: true });
        var resData = (createRes && createRes.data) || {};
        // Single-event create returns the event with `id`; recurring create returns count + ids.
        createdEventId = resData.id || null;
        if (Array.isArray(resData.ids) && resData.ids.length > 0) {
          createdEventIds = resData.ids;
        }
      }

      // Optionally send invite to linked co-parent.
      // - Recurring events → ONE bulk share covering every occurrence (single notification).
      // - Single event → existing single-share endpoint.
      if (sendToCoparent && coparentLink) {
        try {
          if (createdEventIds && createdEventIds.length > 0) {
            var bulkRes = await axios.post(
              API + '/calendar/events/share-bulk',
              { event_ids: createdEventIds },
              { withCredentials: true }
            );
            var bulkCount = (bulkRes && bulkRes.data && bulkRes.data.count) || createdEventIds.length;
            toast.success('Invite sent to your co-parent (' + bulkCount + ' occurrences in one notification)');
          } else if (createdEventId) {
            await axios.post(API + '/calendar/events/' + createdEventId + '/share', {}, { withCredentials: true });
            toast.success('Invite sent to your co-parent');
          }
        } catch (shareErr) {
          var msg = (shareErr && shareErr.response && shareErr.response.data && shareErr.response.data.detail) || 'Could not send invite to co-parent.';
          toast.error(msg);
        }
      }

      setEventForm({ title: '', category: 'Exchange', start_date: '', start_time: '', description: '', notes: '', color: '', recurrence: '', recurrence_days: [], recurrence_end_date: '', cancelled: false });
      setSendToCoparent(false);
      setShowEventForm(false);
      setEditingEvent(null);
      loadData();
    } catch (err) { console.error(err); }
  };

  var handleDeleteEvent = async function(id) {
    if (!window.confirm('Move this event to Recently Deleted?')) return;
    try {
      await axios.delete(API + '/calendar/' + id, { withCredentials: true });
      loadData();
      loadDeleted();
    } catch (err) { console.error(err); }
  };

  var handleRestoreEvent = async function(id) {
    try {
      await axios.post(API + '/calendar/events/' + id + '/restore', {}, { withCredentials: true });
      loadData();
      loadDeleted();
    } catch (err) { console.error(err); }
  };

  var handlePermanentDeleteEvent = async function(id) {
    if (!window.confirm('Permanently delete this event? This is irreversible.')) return;
    try {
      await axios.delete(API + '/calendar/events/' + id + '/permanent', { withCredentials: true });
      loadDeleted();
    } catch (err) { console.error(err); }
  };

  var batchRestore = async function(ids) {
    try {
      await axios.post(API + '/calendar/batch-restore', { ids: ids }, { withCredentials: true });
      setSelectedDeleted([]);
      loadData();
      loadDeleted();
    } catch (err) { console.error(err); }
  };

  var batchPermanentDelete = async function(ids) {
    try {
      await axios.post(API + '/calendar/batch-permanent-delete', { ids: ids }, { withCredentials: true });
      setSelectedDeleted([]);
      loadDeleted();
    } catch (err) { console.error(err); }
  };


  var startEditEvent = function(ev) {
    setEditingEvent(ev.id);
    setEventForm({ title: ev.title, category: ev.category, start_date: ev.start_date || '', start_time: ev.start_time || '', description: ev.description || '', notes: ev.notes || '', color: ev.color || '', recurrence: ev.recurrence || '', recurrence_days: ev.recurrence_days || [], recurrence_end_date: ev.recurrence_end_date || '', cancelled: ev.cancelled || false });
    setShowEventForm(true);
  };

  var handleImportFile = async function(fileObj) {
    if (!fileObj || importing) return;
    setImporting(true);
    setImportResult(null);
    try {
      var formData = new FormData();
      formData.append('file', fileObj);
      var res = await axios.post(API + '/calendar/import-ics', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      loadData();
    } catch (e) {
      setImportResult({ error: e.response && e.response.data ? e.response.data.detail : 'Import failed' });
    }
    setImporting(false);
  };

  var handleImportUrl = async function() {
    if (!importUrl.trim() || importing) return;
    setImporting(true);
    setImportResult(null);
    try {
      var res = await axios.post(API + '/calendar/import-url', { url: importUrl }, { withCredentials: true });
      setImportResult(res.data);
      setImportUrl('');
      loadData();
    } catch (e) {
      setImportResult({ error: e.response && e.response.data ? e.response.data.detail : 'Import failed' });
    }
    setImporting(false);
  };

  var prevMonth = function() { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  var nextMonth = function() { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  var goToday = function() { setMonth(now.getMonth()); setYear(now.getFullYear()); };

  var daysInMonth = new Date(year, month + 1, 0).getDate();
  var firstDow = new Date(year, month, 1).getDay();
  var cells = [];
  for (var b = 0; b < firstDow; b++) cells.push(null);
  for (var dd = 1; dd <= daysInMonth; dd++) cells.push(dd);

  var todayKey = dateKey(now.getFullYear(), now.getMonth(), now.getDate());
  // Counts — an "exchange" day counts toward total Exchanges AND toward the overnight
  // total of whichever parent is assigned the day immediately after (next calendar day,
  // even if that day falls in the following month).
  var meCt = 0, coCt = 0, exCt = 0;
  for (var cd = 1; cd <= daysInMonth; cd++) {
    var ck = dateKey(year, month, cd);
    var val = assignments[ck];
    if (val === 'me') meCt++;
    else if (val === 'coparent') coCt++;
    else if (val === 'exchange') {
      exCt++;
      var nextDate = new Date(year, month, cd + 1);
      var nextKey = dateKey(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate());
      var nextVal = assignments[nextKey];
      if (nextVal === 'me') meCt++;
      else if (nextVal === 'coparent') coCt++;
    }
  }

  var monthEvents = events.filter(function(ev) {
    if (!ev.start_date) return false;
    var ed = new Date(ev.start_date);
    return ed.getMonth() === month && ed.getFullYear() === year;
  });

  return (
    <div className="space-y-6" data-testid="calendar-page">

      {/* Header with Tabs */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <CalIcon className="w-6 h-6 text-[#5A4E9E]" />
              <h2 className="text-2xl font-bold">Calendar</h2>
            </div>
            <p className="text-white/60">Track overnights, exchanges, and events. Click days to cycle colors.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <button onClick={function() { setCalTab('calendar'); }}
            className={'w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-0 ' +
              (calTab === 'calendar' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
            data-testid="tab-calendar">
            <CalIcon className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">Calendar</span>
          </button>
          <button onClick={function() { setCalTab('changes'); }}
            className={'w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-0 ' +
              (calTab === 'changes' ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
            data-testid="tab-changes">
            <ArrowRightLeft className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">Change Requests</span>
          </button>
          <button onClick={function() { setCalTab('invites'); }}
            className={'relative w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-0 ' +
              (calTab === 'invites' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
            data-testid="tab-invites">
            <CalIcon className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">Pending Invites</span>
            {pendingShares.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold" data-testid="tab-invites-badge">
                {pendingShares.length}
              </span>
            )}
          </button>
          <button onClick={function() { setCalTab('shared'); }}
            className={'w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-0 ' +
              (calTab === 'shared' ? 'bg-[#2D2560]/20 text-[#2D2560] border border-[#2D2560]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
            data-testid="tab-shared">
            <Users className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">Shared with Co-Parent</span>
          </button>
          <button onClick={function() { setShowImport(true); setImportResult(null); }}
            className="col-span-2 lg:col-span-1 w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-0 bg-white/5 text-[#3A3566] border border-white/10 hover:bg-white/10"
            data-testid="import-calendar-btn">
            <Upload className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">Import</span>
          </button>
          <button onClick={function() { setCalTab('google'); }}
            className={'col-span-2 lg:col-span-1 w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-0 ' +
              (calTab === 'google' ? 'bg-[#4285F4]/20 text-[#4285F4] border border-[#4285F4]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
            data-testid="tab-google">
            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">Google Calendar</span>
          </button>
        </div>
      </div>

      {calTab === 'changes' && <ChangeRequestsPanel />}

      {calTab === 'invites' && (
        <div className="card-surface p-5" data-testid="invites-panel">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <CalIcon className="w-5 h-5 text-[#5A4E9E]" />
              <h3 className="text-base font-semibold text-[#2D2560]">Event Invites from Co-Parent</h3>
              <span className="text-xs text-[#3A3566]/80">{pendingShares.length} pending</span>
            </div>
            {pendingShares.length > 0 && (
              <div className="flex gap-2">
                <button onClick={function() { respondToSharesBulk('accept'); }}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white text-xs font-semibold hover:opacity-90 flex items-center gap-1.5"
                  data-testid="invites-accept-all">
                  <CheckCircle className="w-3.5 h-3.5" /> Accept All
                </button>
                <button onClick={function() { respondToSharesBulk('decline'); }}
                  className="px-4 py-2 rounded-lg border border-white/15 text-[#3A3566] text-xs font-semibold hover:bg-white/5 flex items-center gap-1.5"
                  data-testid="invites-decline-all">
                  <X className="w-3.5 h-3.5" /> Decline All
                </button>
              </div>
            )}
          </div>

          {pendingShares.length === 0 ? (
            <div className="text-center py-8">
              <CalIcon className="w-10 h-10 text-[#5A4E9E]/30 mx-auto mb-2" />
              <p className="text-sm text-[#3A3566]/70">No pending event invites. When your co-parent sends you an event, it will show up here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingShares.map(function(ps) {
                var snap = ps.event_snapshot || {};
                var recCount = ps.recurrence_count || (Array.isArray(ps.source_event_ids) ? ps.source_event_ids.length : 1);
                var isBundle = recCount > 1;
                return (
                  <div key={ps.id} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={'pending-share-' + ps.id}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-[#2D2560] truncate">{snap.title || 'Untitled event'}</p>
                        {isBundle && (
                          <span
                            className="text-[#5A4E9E] border-[#5A4E9E]/40"
                            data-testid={'pending-share-recurring-' + ps.id}>
                            Recurring · {recCount} occurrences
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#3A3566]/80">
                        {isBundle ? 'Starts ' : ''}{snap.start_date || '—'}{snap.start_time ? ' at ' + snap.start_time : ''}
                        {snap.category ? ' · ' + snap.category : ''}
                      </p>
                      <p className="text-xs text-[#3A3566]/60 mt-0.5">From {ps.from_user_name || ps.from_user_email || 'co-parent'}</p>
                      {snap.description ? <p className="text-xs text-[#3A3566]/70 mt-1 line-clamp-2">{snap.description}</p> : null}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={function() { respondToShare(ps.id, 'accept'); }}
                        className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white text-xs font-semibold hover:opacity-90"
                        data-testid={'accept-share-' + ps.id}>Accept</button>
                      <button onClick={function() { respondToShare(ps.id, 'decline'); }}
                        className="px-3 py-1.5 rounded-lg border border-white/15 text-[#3A3566] text-xs font-medium hover:bg-white/5"
                        data-testid={'decline-share-' + ps.id}>Decline</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {calTab === 'google' && <GoogleCalendarPanel />}

      {calTab === 'shared' && (
        <div className="card-surface p-5" data-testid="shared-calendar-panel">
          {sharedCal && sharedCal.linked ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-[#2D2560]" />
                <h3 className="text-base font-semibold text-[#2D2560]">
                  Shared Calendar with {sharedCal.partner_name}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="shared-mine-count">
                  <p className="text-2xl font-bold text-[#5A4E9E]">
                    {sharedCal.events.filter(function (e) { return e.owner === 'me'; }).length}
                  </p>
                  <p className="text-xs text-[#3A3566]/80">Your Events</p>
                </div>
                <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="shared-coparent-count">
                  <p className="text-2xl font-bold text-[#2D2560]">
                    {sharedCal.events.filter(function (e) { return e.owner === 'coparent'; }).length}
                  </p>
                  <p className="text-xs text-[#3A3566]/80">{sharedCal.partner_name.split(' ')[0]}'s Events</p>
                </div>
              </div>

              {sharedCal.events.length === 0 ? (
                <div className="text-center py-8">
                  <CalIcon className="w-10 h-10 text-[#5A4E9E]/30 mx-auto mb-2" />
                  <p className="text-sm text-[#3A3566]/70">No shared events yet. Events you create on your calendar will appear here, alongside those from {sharedCal.partner_name.split(' ')[0]}.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sharedCal.events.map(function (ev) {
                    var isMe = ev.owner === 'me';
                    return (
                      <div key={ev.id} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]"
                        data-testid={'shared-event-' + ev.id}>
                        <span className={'w-2 h-2 rounded-full flex-shrink-0 ' + (isMe ? 'bg-[#5A4E9E]' : 'bg-[#2D2560]')}></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#2D2560] truncate">{ev.title}</p>
                          <p className="text-xs text-[#3A3566]/70">{ev.start_date}{ev.start_time ? ' at ' + ev.start_time : ''}</p>
                        </div>
                        {ev.category && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-[#3A3566]/80 border border-white/10">{ev.category}</span>
                        )}
                        <span className={'text-xs font-semibold ' + (isMe ? 'text-[#5A4E9E]' : 'text-[#2D2560]')}>
                          {isMe ? 'You' : sharedCal.partner_name.split(' ')[0]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12" data-testid="shared-not-linked">
              <Users className="w-12 h-12 text-[#5A4E9E]/30 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#2D2560] mb-1">No co-parent linked yet</p>
              <p className="text-xs text-[#3A3566]/70 max-w-md mx-auto">
                Link your accounts in <span className="font-semibold">Co-Parent Link</span> to start sharing calendar views and coordinating schedules.
              </p>
            </div>
          )}
        </div>
      )}

      {calTab === 'calendar' && (<>

      {/* Import Modal */}
      {showImport && (
        <div className="card-surface p-5" data-testid="import-modal">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Upload className="w-4 h-4 text-[#10B981]" />
              Import Google Calendar
            </h3>
            <button onClick={function() { setShowImport(false); setImportResult(null); }}
              className="text-[#6B6690] hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex gap-2 mb-4">
            <button onClick={function() { setImportTab('file'); setImportResult(null); }}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium ' + (importTab === 'file' ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
              data-testid="import-tab-file">
              Upload .ics File
            </button>
            <button onClick={function() { setImportTab('url'); setImportResult(null); }}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium ' + (importTab === 'url' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-white/5 text-[#3A3566] border border-white/10')}
              data-testid="import-tab-url">
              Paste iCal URL
            </button>
          </div>

          {importTab === 'file' && (
            <div>
              <p className="text-xs text-[#6B6690] mb-3">Export your Google Calendar as .ics: Google Calendar &rarr; Settings &rarr; Export calendar</p>
              <label className={'flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ' +
                (importing ? 'border-white/10 text-[#8C86C4]' : 'border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/5')}>
                <Upload className="w-5 h-5" />
                <span className="text-sm font-medium">{importing ? 'Importing...' : 'Choose .ics File'}</span>
                <input type="file" className="hidden" accept=".ics,.ical,.ifb,.icalendar"
                  onChange={function(e) { if (e.target.files[0]) handleImportFile(e.target.files[0]); }}
                  disabled={importing}
                  data-testid="import-file-input" />
              </label>
            </div>
          )}

          {importTab === 'url' && (
            <div>
              <p className="text-xs text-[#6B6690] mb-3">Get your iCal URL: Google Calendar &rarr; Settings &rarr; calendar &rarr; Secret address in iCal format</p>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Link className="w-4 h-4 text-[#8C86C4] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="url" value={importUrl}
                    onChange={function(e) { setImportUrl(e.target.value); }}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg pl-10 pr-3 py-2.5"
                    placeholder="https://calendar.google.com/calendar/ical/...basic.ics"
                    data-testid="import-url-input" />
                </div>
                <button onClick={handleImportUrl}
                  disabled={importing || !importUrl.trim()}
                  className="px-4 py-2 bg-[#5A4E9E] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
                  data-testid="import-url-submit">
                  {importing ? 'Importing...' : 'Import'}
                </button>
              </div>
            </div>
          )}

          {importResult && !importResult.error && (
            <div className="mt-3 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg flex items-center gap-2" data-testid="import-success">
              <CheckCircle className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">{importResult.imported} events imported{importResult.skipped > 0 ? ', ' + importResult.skipped + ' skipped' : ''}</span>
            </div>
          )}
          {importResult && importResult.error && (
            <div className="mt-3 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg text-sm text-[#EF4444]" data-testid="import-error">
              {importResult.error}
            </div>
          )}
        </div>
      )}

      {/* Quick Pattern Builder (with embedded monthly counters) */}
      <div className="card-surface p-4" data-testid="pattern-section">
        {/* Combined monthly counters — overnights (me/coparent) + exchanges — sit at the
            top of the same panel as the Quick Pattern so users see their current month's
            tallies and can adjust patterns without scrolling. */}
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="pattern-monthly-counters">
          {[
            { key: 'me', count: meCt, type: DAY_TYPES.me },
            { key: 'coparent', count: coCt, type: DAY_TYPES.coparent },
            { key: 'exchange', count: exCt, type: DAY_TYPES.exchange },
          ].map(function(s) {
            return (
              <div key={s.key}
                className="rounded-lg border border-[#DDE1EE] bg-white/60 p-3 flex flex-col"
                data-testid={'stat-' + s.key}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded flex-shrink-0" style={{ background: s.type.gradient }}></span>
                  <span className="text-xs font-medium text-[#1E1A3E] truncate">{s.type.label}</span>
                </div>
                <p className="text-2xl font-bold leading-tight" style={{ color: s.type.text }}>{s.count}</p>
                <p className="text-xs text-[#6B6690]">this month</p>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-[#1E1A3E] uppercase tracking-wider">Quick Pattern</p>
          {lastPatternSnapshot && (
            <button onClick={undoPattern}
              className="px-3 py-1 text-xs bg-[#F97316]/10 border border-[#F97316]/30 text-[#F97316] rounded-lg hover:bg-[#F97316]/20 flex items-center gap-1"
              data-testid="undo-pattern-btn">
              <RotateCcw className="w-3 h-3" /> Undo
            </button>
          )}
        </div>
        <p className="text-xs text-[#6B6690] mb-3">Apply a color to one or more days of the week at a set frequency.</p>

        {/* Color-coding legend (inline so users see what each color means while building) */}
        <div className="flex gap-2 flex-wrap mb-3" data-testid="pattern-color-legend">
          {Object.entries(DAY_TYPES).map(function([key, val]) {
            return (
              <button key={key}
                onClick={function() { setPatternColor(key); }}
                className={'flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all ' + (patternColor === key ? 'border-white/30 bg-white/5' : 'border-white/10 hover:bg-white/[0.03]')}
                data-testid={'pattern-color-chip-' + key}
                aria-pressed={patternColor === key}>
                <span className="w-3 h-3 rounded" style={{ background: val.gradient }}></span>
                <span className="text-xs font-medium" style={{ color: val.text }}>{val.label}</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-2">
            <label className="text-xs font-medium text-[#1E1A3E] mb-1 block">Color</label>
            <select value={patternColor}
              onChange={function(e) { setPatternColor(e.target.value); }}
              className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] text-sm rounded-lg px-3 py-2"
              data-testid="pattern-color-select">
              {Object.entries(DAY_TYPES).map(function([k, v]) { return <option key={k} value={k} className="bg-white text-[#1E1A3E]">{v.label}</option>; })}
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="text-xs font-medium text-[#1E1A3E] mb-1 block">Days</label>
            <div className="flex gap-1 w-full" data-testid="pattern-days-select">
              {WEEKDAYS.map(function(d, i) {
                var isSelected = patternDays.includes(i);
                return (
                  <button key={i} onClick={function() { togglePatternDay(i); }}
                    className={'flex-1 min-w-0 px-1 sm:px-2 py-2 text-[11px] sm:text-xs rounded-lg border font-medium transition-all ' + (isSelected ? 'bg-[#5A4E9E]/30 border-[#5A4E9E]/60 text-white' : 'bg-white/[0.06] border-white/15 text-[#3A3566] hover:text-white hover:bg-white/10')}
                    data-testid={'pattern-day-' + d.toLowerCase()}>
                    {d.slice(0, 2)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-2">
            <label className="text-xs font-medium text-[#1E1A3E] mb-1 block">Frequency</label>
            <select value={patternFreq}
              onChange={function(e) { setPatternFreq(e.target.value); }}
              className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] text-sm rounded-lg px-3 py-2"
              data-testid="pattern-freq-select">
              {PATTERN_OPTIONS.map(function(o) { return <option key={o.value} value={o.value} className="bg-white text-[#1E1A3E]">{o.label}</option>; })}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="text-xs font-medium text-[#1E1A3E] mb-1 block">Starting On</label>
            <input type="date" value={patternStart}
              onChange={function(e) { setPatternStart(e.target.value); }}
              className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] text-sm rounded-lg px-3 py-2"
              data-testid="pattern-start-date" />
          </div>
          {/* Apply Pattern — sits at the end of the pattern selections row. */}
          <div className="lg:col-span-3">
            <button onClick={applyPattern} disabled={!patternFreq || patternDays.length === 0}
              className="w-full px-5 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg hover:opacity-90 text-sm font-semibold transition-opacity disabled:bg-none disabled:bg-[#DDE1EE] disabled:text-[#8C86C4] disabled:cursor-not-allowed"
              data-testid="apply-pattern-btn">
              Apply Pattern
            </button>
          </div>
          {/* Exchange time — only relevant when the pattern color is "exchange". */}
          {patternColor === 'exchange' && (
            <div className="lg:col-span-12" data-testid="pattern-exchange-time-wrap">
              <label className="text-xs font-medium text-[#1E1A3E] mb-1 block">Exchange Time</label>
              <select value={patternExchangeTime}
                onChange={function(e) { setPatternExchangeTime(e.target.value); }}
                className="w-full max-w-xs bg-white border border-[#DDE1EE] text-[#1E1A3E] text-sm rounded-lg px-3 py-2"
                data-testid="pattern-exchange-time-select">
                <option value="" className="bg-white text-[#1E1A3E]">No time</option>
                {EXCHANGE_TIME_PRESETS.map(function(t) { return <option key={t} value={t} className="bg-white text-[#1E1A3E]">{t}</option>; })}
              </select>
            </div>
          )}
        </div>

        {/* Add Event action + description side-by-side. */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <button onClick={function() { setShowEventForm(!showEventForm); setEditingEvent(null); setEventForm({ title: '', category: 'Exchange', start_date: '', description: '', color: '', recurrence: '', recurrence_days: [] }); }}
            className="flex-shrink-0 px-5 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 self-start sm:self-auto"
            data-testid="pattern-add-event-btn">
            <Plus className="w-4 h-4" /> Add Event
          </button>
          <p className="text-xs text-[#6B6690] leading-relaxed" data-testid="add-event-hint">
            Create custom recurring events that can be shared and synced with your co-parent.
          </p>
        </div>

        {/* Divider before the embedded calendar grid */}
        <div className="my-5 border-t border-[#DDE1EE]"></div>

        {/* Slim status row sitting above the calendar grid */}
        <div className="flex items-center gap-2 flex-wrap text-xs text-[#6B6690] mb-2">
          <span>Click a day to view summary &amp; conflicts</span>
          {saving && <span className="text-[#6B6690] animate-pulse">· Saving…</span>}
        </div>

        {/* Calendar Grid (now embedded inside the Quick Pattern card) */}
        <div className="rounded-lg border border-[#DDE1EE] overflow-hidden bg-white" data-testid="calendar-grid">
          <div className="flex items-center justify-between p-4 border-b border-[#DDE1EE]">
          <button onClick={prevMonth} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="cal-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <h3 className="text-lg font-bold" data-testid="cal-month-label">{MONTHS[month]} {year}</h3>
            <button onClick={goToday} className="text-xs text-[#5A4E9E] hover:underline" data-testid="cal-today-btn">Today</button>
          </div>
          <button onClick={nextMonth} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="cal-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7 bg-[#2D2560] border-b border-white/10">
          {WEEKDAYS.map(function(dn) {
            return <div key={dn} className="text-center text-xs text-white py-2 font-bold uppercase tracking-wider">{dn}</div>;
          })}
        </div>
        <div className="grid grid-cols-7">
          {cells.map(function(day, idx) {
            if (day === null) return <div key={'e' + idx} className="border-r border-b border-white/5 min-h-[60px] sm:min-h-[190px]"></div>;
            var key = dateKey(year, month, day);
            var assigned = assignments[key];
            var dayType = assigned ? DAY_TYPES[assigned] : null;
            var isToday = key === todayKey;
            var dayEvents = events.filter(function(ev) { return ev.start_date && ev.start_date.startsWith(key); });
            var dow = new Date(year, month, day).getDay();
            var exTime = assigned === 'exchange' ? exchangeTimes[dow] : '';
            // Mobile: cap visible events to 2 so full labels can wrap and remain readable inside the square.
            var mobileMax = 2;
            var desktopMax = 8;

            return (
              <div key={key}
                data-testid={'cal-day-' + day}
                onClick={function() { fetchDaySummary(key); }}
                className={'relative border-r border-b border-white/5 min-h-[60px] sm:min-h-[190px] p-1 sm:p-1.5 cursor-pointer transition-all hover:bg-white/5 ' + (isToday ? 'ring-2 ring-inset ring-[#5A4E9E]' : '') + (selectedDay === key ? ' bg-white/5 ring-2 ring-inset ring-[#2D2560]/55' : '')}>
                <div className="flex items-start justify-between">
                  <span className={'text-[11px] sm:text-[13px] font-medium ' + (isToday ? 'bg-[#5A4E9E] text-white w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center' : 'text-[#3A3566]')}>
                    {day}
                  </span>
                  {dayType && <span className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-sm flex-shrink-0" style={{ background: dayType.gradient }}></span>}
                </div>
                {exTime && (
                  <div className="text-[8px] px-1 py-0.5 rounded bg-[#F97316]/20 text-[#F97316] flex items-center gap-0.5">
                    <Clock className="w-2 h-2" />{exTime}
                  </div>
                )}
                {holidayMap[key] && (
                  <div className="text-[9px] leading-[11px] sm:text-[14px] sm:leading-[17px] px-1 py-[2px] rounded mt-0.5 font-bold whitespace-normal break-words"
                    style={{ background: '#2D2560', color: '#FFFFFF', wordBreak: 'break-word' }}
                    title={holidayMap[key].name}
                    data-testid={'cal-holiday-' + key}>
                    {holidayMap[key].name}
                  </div>
                )}
                {dayEvents.length > 0 && (
                  <div className="mt-0.5 space-y-[2px]">
                    {/* Mobile: 2 events with wrapping labels */}
                    <div className="sm:hidden">
                      {dayEvents.slice(0, mobileMax).map(function(ev, i) {
                        var evColor = EVENT_COLORS.find(function(c) { return c.value === ev.color; }) || EVENT_COLORS[0];
                        return <div key={i} className="text-[9px] leading-[11px] px-1 py-[1px] rounded cursor-pointer hover:opacity-80 font-bold whitespace-normal break-words mb-[2px]"
                          onDoubleClick={function(e) { e.stopPropagation(); startEditEvent(ev); }}
                          style={{ backgroundColor: ev.cancelled ? '#EF4444' : evColor.bg, color: '#FFFFFF', textDecoration: ev.cancelled ? 'line-through' : 'none', wordBreak: 'break-word' }}>{ev.title}</div>;
                      })}
                      {dayEvents.length > mobileMax && <div className="text-[9px] leading-[11px] text-[#8C86C4] font-semibold">+{dayEvents.length - mobileMax}</div>}
                    </div>
                    {/* Desktop: original 8-event layout */}
                    <div className="hidden sm:block">
                      {dayEvents.slice(0, desktopMax).map(function(ev, i) {
                        var evColor = EVENT_COLORS.find(function(c) { return c.value === ev.color; }) || EVENT_COLORS[0];
                        return <div key={i} className="text-[14px] leading-[17px] px-1 py-[2px] rounded truncate cursor-pointer hover:opacity-80 font-bold mb-[2px]"
                          onDoubleClick={function(e) { e.stopPropagation(); startEditEvent(ev); }}
                          style={{ backgroundColor: ev.cancelled ? '#EF4444' : evColor.bg, color: '#FFFFFF', textDecoration: ev.cancelled ? 'line-through' : 'none' }}>{ev.title}</div>;
                      })}
                      {dayEvents.length > desktopMax && <div className="text-[12px] leading-[15px] text-[#8C86C4]">+{dayEvents.length - desktopMax}</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {/* Reschedule Modal */}
      {reschedule && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={function() { setReschedule(null); }}
          data-testid="reschedule-modal-backdrop">
          <div className="bg-white rounded-2xl border border-[#DDE1EE] w-full max-w-md p-6 shadow-2xl"
            onClick={function(e) { e.stopPropagation(); }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1A1738]">Reschedule event</h3>
              <button onClick={function() { setReschedule(null); }} className="text-[#6B6690] hover:text-[#2D2560]" data-testid="reschedule-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[#3A3566] mb-4">
              <span className="font-semibold">{reschedule.event.title}</span>
              <span className="text-[#6B6690]"> · currently {reschedule.event.start_date}{reschedule.event.start_time ? ' at ' + reschedule.event.start_time : ''}</span>
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[#6B6690] mb-1.5">New date</label>
                <input type="date"
                  value={reschedule.newDate}
                  onChange={function(e) { setReschedule(Object.assign({}, reschedule, { newDate: e.target.value })); }}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] rounded-lg px-3 py-2 text-sm"
                  data-testid="reschedule-new-date" />
              </div>
              <div>
                <label className="block text-xs text-[#6B6690] mb-1.5">New time (optional)</label>
                <input type="time"
                  value={reschedule.newTime}
                  onChange={function(e) { setReschedule(Object.assign({}, reschedule, { newTime: e.target.value })); }}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] rounded-lg px-3 py-2 text-sm"
                  data-testid="reschedule-new-time" />
              </div>

              {reschedule.event.recurrence_group_id && (
                <div className="pt-2 border-t border-[#DDE1EE]">
                  <p className="text-xs font-semibold text-[#1E1A3E] uppercase tracking-wider mb-2">Apply to</p>
                  <label className="flex items-start gap-2 cursor-pointer mb-2">
                    <input type="radio" name="reschedule-scope"
                      checked={reschedule.scope === 'single'}
                      onChange={function() { setReschedule(Object.assign({}, reschedule, { scope: 'single' })); }}
                      className="mt-0.5"
                      data-testid="reschedule-scope-single" />
                    <span className="text-sm text-[#1A1738]">
                      <span className="font-medium">Only this event</span>
                      <span className="block text-xs text-[#6B6690]">Other occurrences keep their original dates.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="reschedule-scope"
                      checked={reschedule.scope === 'future'}
                      onChange={function() { setReschedule(Object.assign({}, reschedule, { scope: 'future' })); }}
                      className="mt-0.5"
                      data-testid="reschedule-scope-future" />
                    <span className="text-sm text-[#1A1738]">
                      <span className="font-medium">This event and all future events</span>
                      <span className="block text-xs text-[#6B6690]">Shifts every later occurrence in the series by the same number of days.</span>
                    </span>
                  </label>
                </div>
              )}

              {coparentLink && (
                <label className="flex items-center gap-2 pt-2 border-t border-[#DDE1EE] cursor-pointer">
                  <input type="checkbox"
                    checked={reschedule.notify}
                    onChange={function(e) { setReschedule(Object.assign({}, reschedule, { notify: e.target.checked })); }}
                    data-testid="reschedule-notify" />
                  <span className="text-sm text-[#1A1738]">Notify my co-parent of the change</span>
                </label>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={function() { setReschedule(null); }}
                className="px-4 py-2 text-sm font-medium text-[#6B6690] hover:text-[#2D2560]"
                data-testid="reschedule-cancel">
                Cancel
              </button>
              <button onClick={submitReschedule}
                disabled={rescheduleSaving || !reschedule.newDate}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #2D2560, #3A3178, #5A4E9E)' }}
                data-testid="reschedule-submit">
                {rescheduleSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Day Summary Popup */}
      {selectedDay && (() => {
        var selectedDayEvents = events.filter(function(ev) { return ev.start_date && ev.start_date.startsWith(selectedDay); });
        return (
        <div className="card-surface p-4" data-testid="day-summary-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#2D2560]" />
              <h4 className="text-sm font-semibold text-white">AI Daily Summary — {selectedDay}</h4>
            </div>
            <div className="flex items-center gap-2">
              {assignments[selectedDay] && (
                <button onClick={function() { clearDay(selectedDay); }}
                  className="px-2.5 py-1 text-xs bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/20 flex items-center gap-1"
                  data-testid="clear-day-btn">
                  <Trash2 className="w-3 h-3" /> Clear Day
                </button>
              )}
              <button onClick={function() { setSelectedDay(null); }} className="text-[#8C86C4] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {summaryLoading && !daySummaries[selectedDay] ? (
            <div className="flex items-center gap-2 py-3"><div className="w-4 h-4 border-2 border-[#2D2560] border-t-transparent rounded-full animate-spin"></div><span className="text-xs text-[#6B6690]">Analyzing conflicts...</span></div>
          ) : daySummaries[selectedDay] ? (
            <div className="space-y-3">
              <div className={'flex items-center gap-2 px-3 py-2 rounded-lg border ' + (
                daySummaries[selectedDay].conflict_status === 'red' ? 'bg-red-500/10 border-red-500/30' :
                daySummaries[selectedDay].conflict_status === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' :
                'bg-emerald-500/10 border-emerald-500/30'
              )}>
                <span className={'w-2.5 h-2.5 rounded-full ' + (
                  daySummaries[selectedDay].conflict_status === 'red' ? 'bg-red-500' :
                  daySummaries[selectedDay].conflict_status === 'yellow' ? 'bg-yellow-500' :
                  'bg-emerald-500'
                )}></span>
                <span className={'text-xs font-medium ' + (
                  daySummaries[selectedDay].conflict_status === 'red' ? 'text-red-400' :
                  daySummaries[selectedDay].conflict_status === 'yellow' ? 'text-yellow-400' :
                  'text-emerald-400'
                )}>
                  {daySummaries[selectedDay].conflict_status === 'red' ? 'Conflicting Events' :
                   daySummaries[selectedDay].conflict_status === 'yellow' ? 'Potential Conflict' :
                   'No Conflicts'}
                </span>
              </div>
              <p className="text-sm text-[#3A3566]">{daySummaries[selectedDay].summary}</p>
              {daySummaries[selectedDay].conflict_reason && (
                <p className="text-xs text-[#6B6690] italic">{daySummaries[selectedDay].conflict_reason}</p>
              )}
              {/* Federal holiday banner — appears above the day's events with a "Remove" action. */}
              {holidayMap[selectedDay] && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                  style={{ background: '#2D2560', borderColor: '#2D2560' }}
                  data-testid={'day-holiday-' + selectedDay}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate text-white">{holidayMap[selectedDay].name}</p>
                    <p className="text-[11px] text-white/80">US federal holiday</p>
                  </div>
                  <button
                    onClick={function() { dismissHoliday(holidayMap[selectedDay].id); }}
                    className="text-xs font-bold px-2 py-1 rounded text-white hover:bg-white/15"
                    data-testid={'day-holiday-dismiss-' + selectedDay}>
                    Remove
                  </button>
                </div>
              )}
              {/* Full list of every event scheduled for this day */}
              <div className="pt-2 border-t border-[color:var(--border-subtle)]" data-testid="day-events-list">
                <h5 className="text-xs font-semibold uppercase tracking-wider text-[#6B6690] mb-2">
                  Events on this day ({selectedDayEvents.length})
                </h5>
                {selectedDayEvents.length === 0 ? (
                  <p className="text-xs text-[#8C86C4] italic">No events scheduled.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {selectedDayEvents.map(function(ev) {
                      var evColor = EVENT_COLORS.find(function(c) { return c.value === ev.color; }) || EVENT_COLORS[0];
                      var startTime = ev.start_date && ev.start_date.length > 10 ? ev.start_date.slice(11, 16) : null;
                      return (
                        <li key={ev.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg border border-[color:var(--border-subtle)] bg-white"
                          data-testid={'day-event-' + ev.id}>
                          <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: evColor.hex }}></span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={'text-sm font-medium ' + (ev.cancelled ? 'line-through text-[#8C86C4]' : 'text-[#1E1A3E]')}>{ev.title}</span>
                              {ev.category && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: evColor.bg, color: '#FFFFFF' }}>
                                  {ev.category}
                                </span>
                              )}
                              {startTime && (
                                <span className="text-xs text-[#6B6690]">{startTime}</span>
                              )}
                              {ev.cancelled && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#EF4444]">Cancelled</span>
                              )}
                            </div>
                            {ev.description && (
                              <p className="text-xs text-[#6B6690] mt-0.5 leading-snug">{ev.description}</p>
                            )}
                          </div>
                          <button onClick={function() { openReschedule(ev); }}
                            className="text-xs text-[#5A4E9E] hover:text-[#2D2560] font-medium flex-shrink-0"
                            data-testid={'day-event-reschedule-' + ev.id}>
                            Reschedule
                          </button>
                          <button onClick={function() { startEditEvent(ev); }}
                            className="text-xs text-[#6B6690] hover:text-[#2D2560] font-medium flex-shrink-0"
                            data-testid={'day-event-edit-' + ev.id}>
                            Edit
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[#6B6690]">Click the dot on a calendar day to get an AI summary.</p>
          )}
        </div>
        );
      })()}

      {/* Events */}
      {/* Events This Month panel removed per user request */}

      {/* Clear & Recently Deleted */}
      <div className="card-surface p-3 sm:p-4 space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h3 className="text-xs sm:text-sm font-semibold text-[#3A3566] uppercase tracking-wider">Calendar Management</h3>
          <div className="flex gap-2 flex-wrap">
            <button onClick={function() {
                setPrintScope('month');
                setPrintYear(year);
                setPrintMonth(month);
                setPrintYearsAhead(1);
                setShowPrintModal(true);
              }}
              className="px-3 py-1.5 bg-[#2D2560] hover:bg-[#1A1738] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
              data-testid="print-pdf-btn">
              <Printer className="w-3.5 h-3.5" /> Print to PDF
            </button>
            <button onClick={clearMonth}
              className="px-3 py-1.5 bg-white/5 border border-white/10 text-[#3A3566] hover:bg-white/10 rounded-lg text-xs transition-colors"
              data-testid="clear-month-btn">
              Clear {MONTHS[month]}
            </button>
            <button onClick={function() { setClearMode(clearMode ? '' : 'all'); setClearConfirmText(''); }}
              className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg text-xs transition-colors"
              data-testid="clear-all-btn">
              Clear All
            </button>
          </div>
        </div>
        {clearMode === 'all' && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 space-y-3" data-testid="clear-all-confirm">
            <p className="text-sm text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> This will move ALL events to Recently Deleted.</p>
            <p className="text-xs text-[#6B6690]">Type <span className="font-mono text-red-400">DELETE ALL EVENTS</span> to confirm:</p>
            <div className="flex gap-2">
              <input value={clearConfirmText} onChange={function(e) { setClearConfirmText(e.target.value); }}
                className="flex-1 bg-white border border-[#DDE1EE] rounded-lg px-3 py-2 text-sm text-[#1E1A3E] font-mono placeholder:text-[#A8AAD4] focus:outline-none focus:border-red-500/50"
                placeholder="DELETE ALL EVENTS" data-testid="clear-all-confirm-input" />
              <button onClick={clearAll} disabled={clearConfirmText !== 'DELETE ALL EVENTS'}
                className="px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-sm hover:bg-red-500/30 disabled:opacity-30 transition-colors"
                data-testid="clear-all-confirm-btn">
                Confirm
              </button>
              <button onClick={function() { setClearMode(''); setClearConfirmText(''); }}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-[#3A3566] hover:bg-white/10">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recently Deleted Events — Collapsible */}
      {deletedEvents.length > 0 && (
        <div className="card-surface overflow-hidden" data-testid="calendar-recently-deleted">
          <button onClick={function() { setDeletedOpen(!deletedOpen); }}
            className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            data-testid="deleted-toggle-btn">
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-400" />
              <span className="text-sm font-semibold">Recently Deleted</span>
              <span className="text-xs text-[#6B6690]">({deletedEvents.length})</span>
            </div>
            <ChevronDown className={'w-4 h-4 text-[#6B6690] transition-transform ' + (deletedOpen ? 'rotate-180' : '')} />
          </button>

          {deletedOpen && (
            <div className="border-t border-white/10 p-3 sm:p-4 space-y-3">
              {/* Batch Actions */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox"
                      checked={selectedDeleted.length === deletedEvents.length && deletedEvents.length > 0}
                      onChange={function() {
                        if (selectedDeleted.length === deletedEvents.length) setSelectedDeleted([]);
                        else setSelectedDeleted(deletedEvents.map(function(e) { return e.id; }));
                      }}
                      className="w-3.5 h-3.5 rounded accent-[#5A4E9E]"
                      data-testid="select-all-deleted" />
                    <span className="text-xs text-[#3A3566]">Select all</span>
                  </label>
                  {selectedDeleted.length > 0 && (
                    <span className="text-xs text-[#8C86C4]">{selectedDeleted.length} selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {selectedDeleted.length > 0 && (
                    <>
                      <button onClick={function() { batchRestore(selectedDeleted); }}
                        className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 rounded-lg text-xs flex items-center gap-1"
                        data-testid="batch-restore-btn">
                        <RotateCcw className="w-3 h-3" /> Restore ({selectedDeleted.length})
                      </button>
                      <button onClick={function() { batchPermanentDelete(selectedDeleted); }}
                        className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg text-xs flex items-center gap-1"
                        data-testid="batch-delete-btn">
                        <Trash2 className="w-3 h-3" /> Delete ({selectedDeleted.length})
                      </button>
                    </>
                  )}
                  <button onClick={function() { if (window.confirm('Permanently delete ALL ' + deletedEvents.length + ' items?')) batchPermanentDelete(deletedEvents.map(function(e) { return e.id; })); }}
                    className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg text-xs flex items-center gap-1"
                    data-testid="batch-delete-all-btn">
                    <AlertTriangle className="w-3 h-3" /> Delete All
                  </button>
                  <button onClick={function() { if (window.confirm('Restore ALL ' + deletedEvents.length + ' items?')) batchRestore(deletedEvents.map(function(e) { return e.id; })); }}
                    className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 rounded-lg text-xs flex items-center gap-1"
                    data-testid="batch-restore-all-btn">
                    <RotateCcw className="w-3 h-3" /> Restore All
                  </button>
                </div>
              </div>

              {/* Event List */}
              <div className="space-y-1.5">
                {deletedEvents.map(function(ev) {
                  var isSelected = selectedDeleted.indexOf(ev.id) !== -1;
                  return (
                    <div key={ev.id} className={'flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ' + (isSelected ? 'bg-white/5 border-[#5A4E9E]/30' : 'bg-white/[0.02] border-white/5')} data-testid={'deleted-event-' + ev.id}>
                      <input type="checkbox" checked={isSelected}
                        onChange={function() {
                          setSelectedDeleted(function(prev) {
                            return isSelected ? prev.filter(function(id) { return id !== ev.id; }) : prev.concat([ev.id]);
                          });
                        }}
                        className="w-3.5 h-3.5 rounded accent-[#5A4E9E] flex-shrink-0"
                        data-testid={'select-deleted-' + ev.id} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#3A3566] truncate">{ev.title}</p>
                        <div className="flex items-center gap-2 text-xs text-[#8C86C4] mt-0.5 flex-wrap">
                          <span>{ev.category}</span>
                          <span>{ev.start_date}</span>
                          <span className="text-red-400">{ev.days_remaining > 0 ? ev.days_remaining + 'd' : 'soon'}</span>
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={function() { handleRestoreEvent(ev.id); }}
                          className="p-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors"
                          data-testid={'restore-event-' + ev.id} title="Restore">
                          <RotateCcw className="w-3 h-3" />
                        </button>
                        <button onClick={function() { handlePermanentDeleteEvent(ev.id); }}
                          className="p-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          data-testid={'perm-delete-event-' + ev.id} title="Delete permanently">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      </>)}

      {/* Event Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-6" data-testid="event-modal">
          <div className="bg-[#FFFFFF] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6 space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-bold">{editingEvent ? 'Edit Event' : 'Add Event'}</h3>
              <button onClick={function() { setShowEventForm(false); setEditingEvent(null); }} className="text-[#6B6690] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleAddEvent} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1.5">Title</label>
                <input type="text" value={eventForm.title}
                  onChange={function(e) { setEventForm(Object.assign({}, eventForm, { title: e.target.value })); }}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., Soccer Practice" required data-testid="event-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1.5">Category</label>
                  <select value={eventForm.category}
                    onChange={function(e) { setEventForm(Object.assign({}, eventForm, { category: e.target.value })); }}
                    className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] rounded-lg px-3 py-2 text-sm" data-testid="event-category-select">
                    {EVENT_CATEGORIES.map(function(cat) { return <option key={cat} value={cat}>{cat}</option>; })}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1.5">Start Date</label>
                  <input type="date" value={eventForm.start_date}
                    onChange={function(e) { setEventForm(Object.assign({}, eventForm, { start_date: e.target.value })); }}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm" required data-testid="event-date-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1.5">Time (optional)</label>
                <input type="time" value={eventForm.start_time || ''}
                  onChange={function(e) { setEventForm(Object.assign({}, eventForm, { start_time: e.target.value })); }}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="event-time-input" />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1.5">Color Code</label>
                <div className="flex gap-2 flex-wrap">
                  {EVENT_COLORS.map(function(c) {
                    var selected = eventForm.color === c.value;
                    return (
                      <button key={c.value} type="button"
                        onClick={function() { setEventForm(Object.assign({}, eventForm, { color: c.value })); }}
                        className={'flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ' +
                          (selected ? 'border-white/30 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10')}
                        style={selected ? { borderColor: c.hex } : {}}
                        data-testid={'event-color-' + (c.value || 'blue')}>
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.hex }}></span>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Recurrence */}
              <div className="bg-white/5 rounded-lg p-3 space-y-2" data-testid="event-recurrence-section">
                <label className="block text-xs sm:text-sm font-medium">Repeat</label>
                <select value={eventForm.recurrence}
                  onChange={function(e) { setEventForm(Object.assign({}, eventForm, { recurrence: e.target.value })); }}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] rounded-lg px-3 py-2 text-sm"
                  data-testid="event-recurrence-select">
                  <option value="">Does not repeat</option>
                  <option value="every">Every week</option>
                  <option value="every-other">Every other week</option>
                  <option value="every-fourth">Every fourth week</option>
                  <option value="yearly">Every year (same date)</option>
                </select>
                {eventForm.recurrence && eventForm.recurrence !== 'yearly' && (
                  <div>
                    <label className="block text-xs text-[#6B6690] mb-1.5">Repeat on days</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {WEEKDAYS.map(function(dn, i) {
                        var isSelected = (eventForm.recurrence_days || []).indexOf(i) !== -1;
                        return (
                          <button key={i} type="button"
                            onClick={function() {
                              var current = eventForm.recurrence_days || [];
                              var updated = isSelected ? current.filter(function(d) { return d !== i; }) : current.concat([i]);
                              setEventForm(Object.assign({}, eventForm, { recurrence_days: updated }));
                            }}
                            className={'w-9 h-9 rounded-lg text-xs font-medium transition-all ' +
                              (isSelected ? 'bg-[#2D2560]/20 text-[#2D2560] border border-[#2D2560]/40' : 'bg-white/5 text-[#6B6690] border border-white/10 hover:bg-white/10')}
                            data-testid={'recurrence-day-' + dn.toLowerCase()}>
                            {dn}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs text-[#6B6690] mb-1.5">End on (optional)</label>
                      <input type="date"
                        value={eventForm.recurrence_end_date || ''}
                        min={eventForm.start_date || undefined}
                        onChange={function(e) { setEventForm(Object.assign({}, eventForm, { recurrence_end_date: e.target.value })); }}
                        className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] rounded-lg px-3 py-2 text-sm"
                        data-testid="event-recurrence-end-date" />
                    </div>
                    <p className="text-xs text-[#8C86C4] mt-2">
                      {eventForm.recurrence_end_date
                        ? 'Events will repeat until ' + eventForm.recurrence_end_date + '.'
                        : 'No end date set — events will be created for 1 year from the start date.'}
                    </p>
                  </div>
                )}
                {eventForm.recurrence === 'yearly' && (
                  <div>
                    <label className="block text-xs text-[#6B6690] mb-1.5">End on (optional)</label>
                    <input type="date"
                      value={eventForm.recurrence_end_date || ''}
                      min={eventForm.start_date || undefined}
                      onChange={function(e) { setEventForm(Object.assign({}, eventForm, { recurrence_end_date: e.target.value })); }}
                      className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] rounded-lg px-3 py-2 text-sm"
                      data-testid="event-recurrence-end-date-yearly" />
                    <p className="text-xs text-[#8C86C4] mt-2">
                      {eventForm.recurrence_end_date
                        ? 'Repeats yearly on the same date until ' + eventForm.recurrence_end_date + '.'
                        : 'No end date set — repeats yearly on the same date for 5 years.'}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1.5">Description (optional)</label>
                <textarea value={eventForm.description}
                  onChange={function(e) { setEventForm(Object.assign({}, eventForm, { description: e.target.value })); }}
                  className="w-full h-16 bg-white/5 border border-white/10 text-white rounded-lg p-3 resize-none text-sm"
                  placeholder="Additional details..." data-testid="event-desc-input" />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-1.5">Notes (optional)</label>
                <textarea value={eventForm.notes || ''}
                  onChange={function(e) { setEventForm(Object.assign({}, eventForm, { notes: e.target.value })); }}
                  className="w-full h-16 bg-white/5 border border-white/10 text-white rounded-lg p-3 resize-none text-sm"
                  placeholder="Private notes about this event..." data-testid="event-notes-input" />
              </div>
              {editingEvent && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02] cursor-pointer" data-testid="event-cancelled-toggle">
                  <input type="checkbox" checked={eventForm.cancelled || false}
                    onChange={function(e) { setEventForm(Object.assign({}, eventForm, { cancelled: e.target.checked })); }}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 accent-[#EF4444]" />
                  <div>
                    <p className="text-sm font-medium text-[#EF4444]">Mark as Cancelled</p>
                    <p className="text-xs text-[#6B6690]">Event will appear with a strikethrough and cancelled badge</p>
                  </div>
                </label>
              )}
              {!editingEvent && coparentLink && (
                <div className="p-3 rounded-lg border border-[#5A4E9E]/30 bg-[#5A4E9E]/5" data-testid="event-send-coparent-toggle">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#2D2560]">Send to co-parent</p>
                      <p className="text-xs text-[#3A3566]/80">They'll get a push notification. If accepted, this event auto-populates on their calendar.</p>
                    </div>
                    <div className="flex shrink-0 rounded-lg border border-[#5A4E9E]/30 overflow-hidden bg-white" role="group" aria-label="Send to co-parent toggle">
                      <button type="button" onClick={function() { setSendToCoparent(true); }}
                        className={'px-3 py-1.5 text-xs font-semibold transition-colors ' + (sendToCoparent
                          ? 'bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white'
                          : 'text-[#3A3566] hover:bg-[#EEEFF8]')}
                        aria-pressed={sendToCoparent}
                        data-testid="send-coparent-yes">Yes</button>
                      <button type="button" onClick={function() { setSendToCoparent(false); }}
                        className={'px-3 py-1.5 text-xs font-semibold transition-colors ' + (!sendToCoparent
                          ? 'bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white'
                          : 'text-[#3A3566] hover:bg-[#EEEFF8]')}
                        aria-pressed={!sendToCoparent}
                        data-testid="send-coparent-no">No</button>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button type="submit"
                  className="flex-1 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 font-semibold rounded-lg py-2 text-sm"
                  data-testid="event-submit-btn">{editingEvent ? 'Save Changes' : 'Add Event'}</button>
                <button type="button" onClick={function() { setShowEventForm(false); setEditingEvent(null); }}
                  className="px-4 sm:px-6 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* === Print to PDF Modal === */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="print-pdf-modal">
          <div className="card-surface p-5 sm:p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1A1738] flex items-center gap-2">
                <Printer className="w-5 h-5 text-[#2D2560]" /> Print Calendar to PDF
              </h3>
              <button onClick={function() { setShowPrintModal(false); }} aria-label="Close"
                className="text-[#1A1738]/60 hover:text-[#1A1738]" data-testid="print-pdf-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-[#3A3566] mb-4">
              Export your calendar with overnights, shared events, change requests, and federal holidays. Includes a per-month summary using the standard Parentum letterhead.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#1A1738] mb-1.5 block">Export Range</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'month', label: 'Single Month' },
                    { v: 'year', label: 'Full Year' },
                    { v: 'multi-year', label: 'Up to 3 Years' },
                  ].map(function (opt) {
                    var active = printScope === opt.v;
                    return (
                      <button key={opt.v} type="button"
                        onClick={function() { setPrintScope(opt.v); }}
                        className={'px-2 py-2 rounded-lg text-xs font-semibold transition-colors border ' + (
                          active
                            ? 'bg-[#2D2560] text-white border-[#2D2560]'
                            : 'bg-white text-[#1A1738] border-[#DDE1EE] hover:border-[#2D2560]/40'
                        )}
                        data-testid={'print-scope-' + opt.v}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {printScope === 'month' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-[#1A1738] mb-1.5 block">Month</label>
                    <select value={printMonth}
                      onChange={function(e) { setPrintMonth(parseInt(e.target.value, 10)); }}
                      className="w-full bg-white border border-[#DDE1EE] rounded-lg py-2 px-3 text-sm text-[#1A1738] focus:outline-none focus:border-[#2D2560]"
                      data-testid="print-month-select">
                      {MONTHS.map(function (mn, i) {
                        return <option key={i} value={i}>{mn}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#1A1738] mb-1.5 block">Year</label>
                    <select value={printYear}
                      onChange={function(e) { setPrintYear(parseInt(e.target.value, 10)); }}
                      className="w-full bg-white border border-[#DDE1EE] rounded-lg py-2 px-3 text-sm text-[#1A1738] focus:outline-none focus:border-[#2D2560]"
                      data-testid="print-year-select">
                      {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2, now.getFullYear() + 3].map(function (yy) {
                        return <option key={yy} value={yy}>{yy}</option>;
                      })}
                    </select>
                  </div>
                </div>
              )}

              {printScope === 'year' && (
                <div>
                  <label className="text-xs font-semibold text-[#1A1738] mb-1.5 block">Year</label>
                  <select value={printYear}
                    onChange={function(e) { setPrintYear(parseInt(e.target.value, 10)); }}
                    className="w-full bg-white border border-[#DDE1EE] rounded-lg py-2 px-3 text-sm text-[#1A1738] focus:outline-none focus:border-[#2D2560]"
                    data-testid="print-year-only-select">
                    {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2, now.getFullYear() + 3].map(function (yy) {
                      return <option key={yy} value={yy}>{yy}</option>;
                    })}
                  </select>
                </div>
              )}

              {printScope === 'multi-year' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-[#1A1738] mb-1.5 block">Starting Year</label>
                    <select value={printYear}
                      onChange={function(e) { setPrintYear(parseInt(e.target.value, 10)); }}
                      className="w-full bg-white border border-[#DDE1EE] rounded-lg py-2 px-3 text-sm text-[#1A1738] focus:outline-none focus:border-[#2D2560]"
                      data-testid="print-multi-year-start">
                      {[now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2].map(function (yy) {
                        return <option key={yy} value={yy}>{yy}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#1A1738] mb-1.5 block">Span</label>
                    <select value={printYearsAhead}
                      onChange={function(e) { setPrintYearsAhead(parseInt(e.target.value, 10)); }}
                      className="w-full bg-white border border-[#DDE1EE] rounded-lg py-2 px-3 text-sm text-[#1A1738] focus:outline-none focus:border-[#2D2560]"
                      data-testid="print-multi-year-span">
                      <option value={1}>1 year</option>
                      <option value={2}>2 years</option>
                      <option value={3}>3 years</option>
                    </select>
                  </div>
                </div>
              )}

              <p className="text-[11px] text-[#1A1738]/55 leading-snug">
                Larger ranges produce longer PDFs — a 3-year export generates roughly 72 pages (one calendar grid + one summary page per month).
              </p>
            </div>

            <div className="flex gap-3 mt-5">
              <button type="button" onClick={handlePrintCalendar} disabled={printing}
                className="flex-1 bg-[#2D2560] hover:bg-[#1A1738] text-white font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                data-testid="print-pdf-confirm">
                {printing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Generating…
                  </>
                ) : (
                  <>
                    <Printer className="w-4 h-4" /> Download PDF
                  </>
                )}
              </button>
              <button type="button" onClick={function() { setShowPrintModal(false); }}
                className="px-4 sm:px-6 bg-white border border-[#DDE1EE] text-[#1A1738] hover:bg-[#F6F7FC] rounded-lg text-sm"
                data-testid="print-pdf-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
